import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ScrollBar, LoadingProgress, Dialog } from '@framework-controls/core';
import { Customer, ServiceDocsData, VehicleRec, RepairOrder, IdmUuids, UpdateApptCustInp, UpdateApptVehInp, ascClickToCallInput, ascCtaInteractionInput } from 'src/models';
import { IconRemove } from 'src/content/icons';
import { IRepairOrdersState } from 'src/store/states';
import EAppBanner from './EApprovalBanner/EAppBanner';
import DocumentViewer from './DocumentViewer/DocumentViewer';
import RepairAdvisor from 'src/components/CurrentRepairCard/RepairAdvisor/RepairAdvisor';
import ServiceDocsMediaBox from './ServiceDocsMediaBox/ServiceDocsMediaBox';
import { ViewMode } from 'src/enums';
import { strings } from 'src/localization';
import * as css from './ServiceDocs.module.scss';
import classNames from 'classnames';
import moment from 'moment';
import 'moment/min/locales.min';
import { useHistory } from 'react-router';
import { ascClickToCall, ascCtaInteraction, getElementColor } from 'src/helpers/EventHelper/EventHelper';

interface IProps {
    viewMode: ViewMode;
    customer?: Customer;
    repairOrders?: IRepairOrdersState;
    loadVin?: string;
    loadRO?: string;
    serviceDocsSpecs?: ServiceDocsData;
    theme: string;
    previewFlag: string;
    store: string;
    branch: string;
    idmDocs: any;
    idmFile: string;
    idmFiles: string[];
    osdWebViewFlag: string;
    locale: string;
    miscdec: string;
    identityVerified: boolean;
    osdVerifyIdentityFlag: boolean;
    selectVehicle: (id: string) => void;
    selectRO: (roNum: string) => void;
    getIDMdoc: (docUUID: string, elementUUID: string) => void;
    getIDMdocs: (idmPdfUUIDs: IdmUuids[]) => void;
    findIDMdocs: (store: string, branch: string, docType: string, key1: string,
        key1value: string, key2: string, key2value: string) => void;
    clearIdmFile: () => void;
    logDocsHistory: (keyRO: string, action: string, docType: string, details: string, idmUrl: string) => void;
    clearROHistory: () => void,
    getDocsROHistory: () => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    updateAppVehicle: (inp: UpdateApptVehInp) => void;
    addCUC: (cucNum: number) => void;
}

const ServiceDocs = (props: IProps & RouteComponentProps) => {
    const mySpecs = props.serviceDocsSpecs;
    const [selectedRO, setSelectedRO] = useState<RepairOrder | undefined>(undefined);
    const [selectedVehicle, setSelectedVehicle] = useState<VehicleRec | undefined>(undefined);
    const [IVModalFlag, setIVModalFlag] = useState(false);
    const [uuidProcessed, setUuidProcessed] = useState(false);
    const [IDMPdfUUIDs, setIDMPdfUUIDs] = useState<IdmUuids[]>([]);
    const [refreshMsg, setRefreshMsg] = useState<boolean>(false);
    const [docsFound, setDocsFound] = useState<boolean>(false);
    const [docTimeRef] = useState(new Date());

    const history = useHistory();

    const phoneClick = useCallback((e: any) => {
        if (selectedRO?.advisorName && selectedVehicle) {
            const ascClickToCallInp : ascClickToCallInput = {
                pageType: 'contact',
                commPhoneNumber: `${selectedRO.advisorPhone} ${selectedRO.advisorPhoneExt}`,
                department: 'service',
                affiliation: 'call_source',
            };
            ascClickToCall(ascClickToCallInp, [selectedVehicle]);
            const elementColor = getElementColor(e, 'color');
            let elementText = selectedRO.advisorPhone;
            if (selectedRO.advisorPhoneExt) {
                elementText = `${selectedRO.advisorPhone} ${selectedRO.advisorPhoneExt}`;
            }
            elementText = elementText ?? '';
            const ascCtaInteractionInp : ascCtaInteractionInput = {
                pageType: 'service',
                elementText: elementText,
                elementColor: elementColor,
                elementOrder: '',
                elementType: 'scheduling_tool',
                elementSubType: 'selection_link',
                eventAction: 'click',
                eventActionResult: 'next_step',
                linkUrl: '',
            };
            ascCtaInteraction(ascCtaInteractionInp, e, [selectedVehicle]);
        }
    }, [selectedRO?.advisorPhone,
        selectedRO?.advisorName,
        selectedRO?.advisorPhoneExt,
        selectedVehicle,
    ]);
    // If we're here by accident then go back to the LandingPage.
    useEffect(() => {
        if (props.viewMode !== ViewMode.ServiceDocs) {
            history.go(0);
        }
    }, [history, props.viewMode]);

    // Increment the Website Visited CUC - 860
    useEffect(() => {
        if (!props.previewFlag) {
            props.addCUC(860);
        }
    }, [props.previewFlag, props.addCUC]);

    // If we came here from a text or email link then we need to log the Website Viewed action
    useEffect(() => {
        if (props.osdWebViewFlag && selectedRO && !props.previewFlag && (!props.osdVerifyIdentityFlag || props.identityVerified)) {
            props.logDocsHistory(selectedRO.RONum, 'WV', '', '', '');
        }
    }, [selectedRO, props.osdWebViewFlag, props.logDocsHistory, props.previewFlag, props.identityVerified,
        props.osdVerifyIdentityFlag]);

    // Get access to the repair order and vehicle.
    useEffect(() => {
        if (props.customer && props.repairOrders && !selectedRO) {
            let repairOrders = props.repairOrders.RepairOrders;
            let selRO: RepairOrder | undefined = undefined;
            // Filter repair orders and select the most recent unless we received an RO in token.
            if (props.loadRO) {
                props.selectRO(props.loadRO);
                setSelectedRO(repairOrders.find(ro => ro.RONum === props.loadRO));
            } else {
                if (repairOrders.length > 1) {
                    if (props.loadVin) {
                        repairOrders = repairOrders.filter(ro => ro.vin === props.loadVin);
                    }
                    // this grabs most recent RO
                    const date = Math.max(...repairOrders.map(ro => Date.parse(ro.RODate)));
                    selRO = repairOrders.find(ro => Date.parse(ro.RODate) === date);
                    setSelectedRO(repairOrders.find(ro => Date.parse(ro.RODate) === date));
                    if (selRO) {
                        if (selRO.RONum !== props.repairOrders.currentRepairOrder) {
                            props.selectRO(selRO.RONum);
                        }
                    }
                } else {
                    setSelectedRO(repairOrders[0]);
                    if (repairOrders[0]) {
                        props.selectRO(repairOrders[0].RONum);
                    }
                }
            }
        }
    }, [props.selectVehicle,
        props.selectRO,
        selectedRO,
        props.customer,
        props.repairOrders,
        props.loadVin,
        props.loadRO]);

    useEffect(() => {
        // Select the vehicle matching loadVin if we have one. Otherwise select the vehicle
        // on the repair order.
        if (props.customer && props.customer.vehicleList && !selectedVehicle) {
            if (props.loadVin) {
                setSelectedVehicle(props.customer.vehicleList.find(veh => veh.vin === props.loadVin));
                const selVeh = props.customer.vehicleList.find(veh => veh.vin === props.loadVin)?.id;
                if (selVeh) {
                    props.selectVehicle(selVeh);
                }
            } else if (selectedRO) {
                if (selectedRO!.vin === '') {
                    setSelectedVehicle(props.customer.vehicleList.find(veh => veh.vin === undefined));
                } else {
                    setSelectedVehicle(props.customer.vehicleList.find(veh => veh.vin === selectedRO!.vin));
                }
                const selVeh = props.customer.vehicleList.find(veh => veh.vin === selectedRO!.vin)?.id;
                if (selVeh) {
                    props.selectVehicle(selVeh);
                }
            }
        }
    }, [selectedRO, selectedVehicle, props.customer, props.loadVin, props.selectVehicle]);

    // Once we have the vehicle we need its history
    useEffect(() => {
        if (selectedRO && selectedVehicle && props.customer) {
            const apptCustInp: UpdateApptCustInp = {
                nameId: props.customer.nameId,
                firstName: props.customer.firstName,
                lastName: props.customer.lastName,
                emailAddress: props.customer.emailAddress,
                phoneNumber: props.customer.phoneNumber,
                phoneType: props.customer.phoneType,
                prefContact: props.customer.prefContact,
            };
            props.onAppCustUpdate(apptCustInp);
            const apptNewVehicle: UpdateApptVehInp = {
                vin: selectedVehicle.vin,
                makeCode: selectedVehicle.makeCode,
                make: selectedVehicle.make,
                model: selectedVehicle.model,
                vehYr: selectedVehicle.year,
                vehColor: selectedVehicle.color,
                mileage: selectedVehicle.prevMileage,
                imagePath: selectedVehicle.imagePath,
                series: selectedVehicle.style,
            };
            props.updateAppVehicle(apptNewVehicle);
            props.clearROHistory();
            props.getDocsROHistory();
        }
    }, [selectedRO,
        selectedVehicle,
        props.customer,
        props.onAppCustUpdate,
        props.updateAppVehicle,
        props.clearROHistory,
        props.getDocsROHistory]);

    // find all of the docs that are set to publish with findAllDocs
    // then retrieve pdf data with getIDMdoc calls on customer demand
    useEffect(() => {
        if (!docsFound && mySpecs && mySpecs.docToken &&
            selectedRO && props.customer?.nameId && props.store && props.branch) {
            // call for all the relevent documents
            const publishedDocTypes: string[] = [];
            mySpecs.documents.forEach((x) => {
                if (x.publish) {
                    publishedDocTypes.push(x.type);
                }
            });
            let findDocNames: string = '';
            publishedDocTypes.forEach((x, index) => {
                switch (x) {
                    case 'RO':
                        findDocNames += '"SVCRO-OSD"';
                        break;
                    case 'SE':
                        findDocNames += '"SPGEST-OSD"';
                        break;
                    case 'MM':
                        findDocNames += '"MMENU-OSD"';
                        break;
                    case 'VI':
                        findDocNames += '"VEHINSPECT-OSD"';
                        break;
                    case 'RC':
                        findDocNames += '"SVCRPTCARD-OSD"';
                        break;
                    case 'VC':
                        findDocNames += '"VCR-OSD"';
                        break;
                    case 'IN':
                        findDocNames += '"SVCINV-OSD"';
                        break;
                    case 'IV':
                        findDocNames += '"RO-VIDEO","RO-IMAGE"';
                        break;
                    case 'HU':
                        findDocNames += '"HUNTER-OSD"';
                        break;
                    default:
                        break;
                }
                if (index !== publishedDocTypes.length-1) {
                    findDocNames += ',';
                }
            });
            props.findIDMdocs(props.store, '"SERVC'+props.branch+'"', findDocNames, 'cust',
                props.miscdec, 'ro', selectedRO.RONum);
            setDocsFound(true);
        }
    }, [docsFound, mySpecs, selectedRO, props.customer, props.miscdec, props.store, props.branch, props.findIDMdocs]);

    useEffect(() => {
        if (props.idmDocs !== '') {
            // go through the idmDocs returned and get the most recent of each type
            let tempUuidObjects: IdmUuids[] = [];
            if (Array.isArray(props.idmDocs?.data?.queryDocument)) {
                props.idmDocs.data.queryDocument.forEach((doc) => {
                    // if the doc is missing uuids then we skip it.
                    if (doc.doc_uuid === undefined || doc.elements[0] === undefined || doc.elements[0].element_uuid === undefined) {
                        return;
                    }
                    let docTypeCode = '';
                    let sortID = 0;
                    switch (doc.doc_type) {
                        case 'SVCRO-OSD':
                            docTypeCode = 'RO';
                            sortID = 1;
                            break;
                        case 'SPGEST-OSD':
                            docTypeCode = 'SE';
                            sortID = 2;
                            break;
                        case 'MMENU-OSD':
                            docTypeCode = 'MM';
                            sortID = 3;
                            break;
                        case 'VEHINSPECT-OSD':
                            docTypeCode = 'VI';
                            sortID = 4;
                            break;
                        case 'SVCRPTCARD-OSD':
                            docTypeCode = 'RC';
                            sortID = 5;
                            break;
                        case 'VCR-OSD':
                            docTypeCode = 'VC';
                            sortID = 6;
                            break;
                        case 'SVCINV-OSD':
                            docTypeCode = 'IN';
                            sortID = 7;
                            break;
                        case 'RO-VIDEO':
                        case 'RO-IMAGE':
                            docTypeCode = 'IV';
                            sortID = 8;
                            break;
                        case 'HUNTER-OSD':
                            docTypeCode = 'HU';
                            sortID = 9;
                            break;
                        default:
                            break;
                    }
                    const typeIndex = tempUuidObjects.findIndex((x) => docTypeCode === x.type);
                    if (typeIndex === -1) {
                        tempUuidObjects.push({
                            docUuid: doc.doc_uuid,
                            elementUuid: doc.elements[0].element_uuid,
                            date: doc.doc_updated,
                            type: docTypeCode,
                            sortOrder: sortID,
                        });
                    } else {
                        // compare dates and replace value if doc has the greater date
                        if (doc.doc_updated > tempUuidObjects[typeIndex].date) {
                            tempUuidObjects[typeIndex] = ({
                                docUuid: doc.doc_uuid,
                                elementUuid: doc.elements[0].element_uuid,
                                date: doc.doc_updated,
                                type: docTypeCode,
                                sortOrder: sortID,
                            });
                        }
                    }
                });
            }
            // if we have Images or Videos returned from the IDM call but no images or
            // videos in the selectedRO then remove the uuids from the list.
            // This may occur because items may not be marked as display to customer. The uuid call
            // doesn't know account for that value.
            if (selectedRO?.IDMPics?.length === 0) {
                tempUuidObjects = tempUuidObjects.filter((uuid) => uuid.type !== 'IV');
            }
            // Sort documents. Order is RO, SE, MM, VI, RC, VC, IN, IV
            tempUuidObjects.sort((a, b) => (a.sortOrder < b.sortOrder) ? -1 : 1);
            setIDMPdfUUIDs(tempUuidObjects);
            setUuidProcessed(true);
        }
    }, [props.idmDocs, selectedRO?.IDMPics]);

    let scrollClasses = classNames(
        css.scrollItems,
    );

    // stuff for jsx
    let eAppBanner: JSX.Element | null = null;
    let documents: JSX.Element | null = null;
    let repairAdvisor: JSX.Element | null = null;
    let openROMessage: JSX.Element | null = null;

    // CSS variables for improved contrast based on theme.
    const roDateCss = classNames(
        css.roDate,
        {
            [css.altContrast]: props.theme === 'lexusMonogram',
            [css.roDateBMW]: props.theme === 'bmw',
            [css.roDateFord]: props.theme === 'ford',


        },
    );
    const footerCss = classNames(
        css.footer,
        {
            [css.altContrast]: props.theme === 'lexusMonogram',
        },
    );

    // We only have selectedRO and selectedVehicle if we have props.customer and props.repairOrders.
    if (mySpecs && selectedRO && selectedVehicle && selectedRO.IDMDocs && (uuidProcessed || !mySpecs.docToken)) {
        const pendingItemsCount = selectedRO.eApprovalSentOrViewedCount ?? 0;
        const vehicleDesc = selectedVehicle.model ?? selectedVehicle.make;
        const advisorPhone = selectedRO.advisorPhone ?? mySpecs.servicePhone;
        const advisorPhoneExt = selectedRO.advisorPhoneExt;
        // Display the date in the following format: July 4, 1774
        const processingDate = new Date(selectedRO.RODate);
        let processingUTCDate = moment.utc(processingDate);
        processingUTCDate = processingUTCDate.locale(props.locale);
        const roDate = processingUTCDate.format('MMMM D, YYYY');

        // If pending eApproval items exist, display the eApproval banner.
        if (selectedRO.eApprovalSentOrViewedCount && selectedRO.eApprovalSentOrViewedCount > 0) {
            eAppBanner =
                <EAppBanner
                    pendingItemsCount={pendingItemsCount}
                    redItems={selectedRO.eApprovalRedItems === 'Y' ? true:false}
                    link={selectedRO.eApprovalLink ? selectedRO.eApprovalLink : ''}
                    previewFlag={props.previewFlag}
                    theme={props.theme}
                />;
            scrollClasses = classNames(
                css.scrollItems,
                css.scrollItemsApproval,
            );
        }

        // If there are no documents to display, show the ServiceDocsNotAvail message instead.
        if (IDMPdfUUIDs.length > 0) {
            // set the documents props to include pics too
            documents =
                <DocumentViewer
                    specs={mySpecs.documents}
                    documents={selectedRO.IDMDocs}
                    idmPdfUUIDs={IDMPdfUUIDs}
                    previewFlag={props.previewFlag}
                    idmFile={props.idmFile}
                    idmFiles={props.idmFiles}
                    roNum={selectedRO.RONum}
                    roDate={selectedRO.RODate}
                    docTimeRef={docTimeRef}
                    getIDMdocs={props.getIDMdocs}
                    setRefreshMsg={setRefreshMsg}
                    setIVModalFlag={setIVModalFlag}
                    getIDMdoc={props.getIDMdoc}
                    clearIdmFile={props.clearIdmFile}
                    logDocsHistory={props.logDocsHistory}
                    addCUC={props.addCUC}
                    theme={props.theme} />;
        } else {
            let notAvailPhone = mySpecs.servicePhone;
            const match = notAvailPhone.match(/^(1|)?(\d{3})?(\d{3})(\d{4})$/);

            if (match) {
                const intlCode = match[1] ? match[1] + '-' : '';
                const areaCode = match[2] ? match[2] + '-' : '';
                notAvailPhone = `${intlCode}${areaCode}${match[3]}-${match[4]}`;
            }
            documents =
                <p className={css.notAvailMessage}>
                    {strings.formatString(strings.ServiceDocsNotAvail, notAvailPhone).toString()}
                </p>;
        }
        if (selectedRO.advisorName) {
            repairAdvisor =
                <RepairAdvisor
                    name={selectedRO.advisorName}
                    phone={advisorPhone}
                    phoneExt={advisorPhoneExt}
                    imageURL={selectedRO.advisorImage}
                    theme={props.theme}
                    ServiceDocs
                    phoneClick={phoneClick}/>;
        }

        const openROMessageStyles = classNames(
            css.openROMessage,
            {
                [css.openROMessageBaseTextWeight]: props.theme === 'bmw' || 'ford' || 'infiniti',
                [css.openROMessageNissan]: props.theme === 'nissan',
            },
        );

        // Display the Open RO Message if the repair order hasn't been closed yet.
        if (!selectedRO.postFlag) {
            openROMessage =
                <div className={openROMessageStyles}>{strings.OpenRepairOrderMessage}</div>;
        }

        let mediaBoxTitle = strings.PicsVideos;
        if (props.serviceDocsSpecs?.documents) {
            const docLocation = props.serviceDocsSpecs?.documents.findIndex(d => d.type === 'IV');
            if (docLocation !== -1) {
                mediaBoxTitle = props.serviceDocsSpecs?.documents[docLocation].displayName;
            }
        }

        const headerBoxStyles = classNames(
            css.headerbox,
            {
                [css.headerboxMazda]: props.theme === 'mazda',
            },
        );

        return (
            <div className={css.mainDiv}>
                <div className={css.mainPane}>
                    {eAppBanner}
                    <ScrollBar scrollVertical>
                        <div className={scrollClasses}>
                            <div className={css.greeting}>
                                <h2 className={props.theme === 'mazda' && css.headerMazda}>
                                    {props.customer?.isCompany ? props.customer.lastName + ',' :
                                    props.customer!.firstName + ','}</h2>
                                <h3>{strings.formatString(strings.ServiceDocsGreeting, vehicleDesc).toString()}</h3>
                            </div>
                            <div className={css.documentsContainer}>
                                <div className={roDateCss}>{roDate}</div>
                                {documents}
                            </div>
                            <div className={css.bottomDiv}>
                                {repairAdvisor}
                                <div className={css.messages}>
                                    {openROMessage}
                                    <div className={css.availMessage}>{mySpecs.availMsg}</div>
                                </div>
                            </div>
                        </div>
                    </ScrollBar>
                    <div className={footerCss}>{mySpecs.footer}</div>
                </div>
                <div>
                    <ServiceDocsMediaBox
                        selectedRO={selectedRO}
                        previewFlag={props.previewFlag}
                        dialogTitle={mediaBoxTitle}
                        locale={props.locale}
                        setIVModalFlag = {setIVModalFlag}
                        IVModalFlag = {IVModalFlag}
                        logDocsHistory = {props.logDocsHistory}
                        addCUC = {props.addCUC}
                        idmDocs = {props.idmDocs}
                        getIDMdoc={props.getIDMdoc}
                        idmFile={props.idmFile}
                        clearIdmFile={props.clearIdmFile}
                        theme={props.theme}
                    ></ServiceDocsMediaBox>
                </div>
                <Dialog
                    id='RefreshMsg'
                    cssClasses={{
                        content: css.refreshContent,
                        paper: css.paper,
                        header: css.LightBoxHeader,
                    }}
                    isOpen={refreshMsg}
                    anchorOrigin={{
                        vertical: 'top',
                    }}
                    header={''}
                    hideSave={true}
                    hideCancel={true}
                    disableUpperCase={true}
                    onCancel={() => setRefreshMsg(false)}
                    onBackDropClick={() => setRefreshMsg(false)}
                    width='full'
                >
                    <div className={headerBoxStyles}>
                        {''}
                        <div className={css.headerIcon}>
                            <IconRemove id='closeBtn' onClick={() => setRefreshMsg(false)}/>
                        </div>
                    </div>
                    <div>{strings.SessionExpired}</div>
                </Dialog>
            </div>
        );
    }

    return (
        <div className={css.mainDiv}>
            <div className={css.mainPane}>
                <LoadingProgress size={100} />
            </div>
        </div>
    );
};

export default ServiceDocs;